import type { Day, ReflectionField } from '../content/data';
import { requiredReflectionIds } from '../progress/progress';
import { useProgress, useProgressActions } from '../progress/progress-context';

function ReflectionInput({ field, required, readOnly }: { field: ReflectionField; required: boolean; readOnly: boolean }) {
  const progress = useProgress();
  const { setReflection } = useProgressActions();
  const value = progress.reflections[field.id] ?? '';

  return (
    <label className="reflection-field">
      <span>
        {field.label}
        {required ? <small>Needed to close the day</small> : null}
      </span>
      {field.input === 'score' ? (
        <input
          type="number"
          min="0"
          max="10"
          value={value}
          required={required}
          disabled={readOnly}
          onChange={(event) => setReflection(field.id, event.currentTarget.value)}
        />
      ) : (
        <textarea
          rows={3}
          value={value}
          required={required}
          disabled={readOnly}
          placeholder="A few honest sentences are enough."
          onChange={(event) => setReflection(field.id, event.currentTarget.value)}
        />
      )}
    </label>
  );
}

export function ReflectionForm({ day, readOnly = false }: { day: Day; readOnly?: boolean }) {
  const requiredIds = new Set(requiredReflectionIds(day));
  const requiredFields = day.reflectionFields.filter((field) => requiredIds.has(field.id));
  const optionalFields = day.reflectionFields.filter((field) => !requiredIds.has(field.id));

  return (
    <section className="reflection-form" aria-labelledby="reflection-heading">
      <div className="section-heading-row">
        <div>
          <h2 id="reflection-heading">Daily retrospective</h2>
          <p>Record enough detail to make tomorrow easier.</p>
        </div>
      </div>

      <div className="reflection-fields">
        {requiredFields.map((field) => (
          <ReflectionInput key={field.id} field={field} required readOnly={readOnly} />
        ))}
      </div>

      {optionalFields.length > 0 ? (
        <details className="more-reflection">
          <summary>{optionalFields.length} more prompts for a deeper review</summary>
          <div className="reflection-fields">
            {optionalFields.map((field) => (
              <ReflectionInput key={field.id} field={field} required={false} readOnly={readOnly} />
            ))}
          </div>
        </details>
      ) : null}
    </section>
  );
}
